import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { srOnly } from '../../components/organic/motion';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { box, button, input, label } from '../shared/styles';

/*
 * The experiment's Culture tab (migration 0104; docs/ANALYSIS_WORKBENCH.md
 * 10g; GET /experiments/{id}/culture; the user's decision R4).
 *
 *   units      by arm, each with its plate and that plate's culture readings,
 *              oldest first: when, the day since the start, the server's clean
 *              area, radius and contamination (⚠ for a contaminated dish), a
 *              tray reading marked, and what each was recorded as. Open shows
 *              the reading's photograph in the analysis workbench's culture
 *              mode (a tray reading in tray mode), its Back coming to this tab
 *              (?experiment=&tab=culture). A
 *              unit with no plate yet says so: its plate is made on Overview.
 *   record     ticked readings' value of one measure into an outcome it fits
 *              (by type and unit), while the experiment runs (POST
 *              /experiments/{id}/observations/from-culture). A contaminated
 *              dish's growth is skipped unless "Include contaminated dishes" is
 *              ticked; contamination itself never is.
 *
 * Only readings the reader's lab scope shows are listed; the others counted.
 */
const when = (r) => (r.observed_at || '').slice(0, 16).replace('T', ' ');
const fmt = (v, digits) => (v === null || v === undefined ? '–' : Number(v).toFixed(digits));

export default function CultureTab({ experimentId, onChanged }) {
  const { t } = useTranslation('experiments');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState([]);
  const [outcome, setOutcome] = useState('');
  const [measure, setMeasure] = useState('');
  const [timepoint, setTimepoint] = useState('');
  const [include, setInclude] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => api.get(`/experiments/${experimentId}/culture`)
    .then(({ data: d }) => { setData(d); setError(''); })
    .catch((e) => setError(errorText(e))), [experimentId]);
  useEffect(() => { load(); }, [load]);

  const arms = useMemo(() => {
    const out = new Map();
    for (const u of data?.units || []) {
      if (!out.has(u.arm)) out.set(u.arm, { key: u.arm, label: u.arm_label || u.arm, units: [] });
      out.get(u.arm).units.push(u);
    }
    return [...out.values()];
  }, [data]);
  const readingsOf = useMemo(() => {
    const out = new Map();
    for (const r of data?.readings || []) out.set(r.unit_id, [...(out.get(r.unit_id) || []), r]);
    return out;
  }, [data]);

  if (error && !data) return <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>;
  if (!data) return <p>{t('loading')}</p>;

  const chosenOutcome = data.outcomes.find((o) => o.key === outcome) || null;
  const measures = chosenOutcome ? chosenOutcome.measures : [];
  const growth = (data.growth_measures || []).includes(measure);
  const chooseOutcome = (key) => {
    setOutcome(key);
    const fits = data.outcomes.find((o) => o.key === key)?.measures || [];
    setMeasure((m) => (fits.includes(m) ? m : fits[0] || ''));
  };
  const toggle = (id) => setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  /** A reading's photograph in the workbench's culture mode -- a tray reading's in tray mode, where another
   *  plate's tray photograph opens -- its Back coming to this tab. */
  const open = (r) => navigate(`/tools/analysis?${new URLSearchParams({
    mode: 'culture', plate: r.plate_id, photo: r.photo_id, src: 'plate', ...(r.tray ? { tray: '1' } : {}),
    back: `${pathname}?${new URLSearchParams({ experiment: experimentId, tab: 'culture' })}`,
  })}`);
  const record = async () => {
    setMessage(null);
    try {
      const { data: done } = await api.post(`/experiments/${experimentId}/observations/from-culture`, {
        analysis_ids: picked, outcome, measure, include_contaminated: include,
        ...(timepoint.trim() ? { timepoint: timepoint.trim() } : {}),
      });
      setMessage({ kind: 'ok', text: t('culture.record.done', { imported: done.imported, already: done.already,
                                                                skipped: (done.skipped_contaminated || []).length }) });
      setPicked([]);
      await load();
      onChanged?.();
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
    }
  };
  const canSend = picked.length > 0 && outcome && measure;
  const outcomeLabel = (key) => data.outcomes.find((o) => o.key === key)?.label || key;

  return (
    <div data-testid="culture-tab">
      <p style={{ fontSize: 13, marginTop: 0 }}>{t('culture.intro')}</p>
      {data.hidden > 0 && <p style={{ fontSize: 13 }} data-testid="culture-hidden">{t('culture.hidden', { count: data.hidden })}</p>}

      {arms.length === 0 && <p style={{ fontSize: 13 }} data-testid="culture-no-units">{t('culture.noUnits')}</p>}
      {arms.map((arm) => (
        <section key={arm.key} style={box} aria-labelledby={`culture-arm-${arm.key}`} data-testid={`culture-arm-${arm.key}`}>
          <h4 id={`culture-arm-${arm.key}`} style={{ marginTop: 0 }}>{t('culture.arm', { label: arm.label, key: arm.key })}</h4>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {arm.units.map((u) => {
              const readings = readingsOf.get(u.unit_id) || [];
              return (
                <li key={u.unit_id} data-testid={`culture-unit-${u.unit_id}`}>
                  <p style={{ margin: '0 0 4px', fontSize: 13 }}>
                    <strong>{u.label}</strong>
                    {' · '}
                    {u.plate_id ? <bdi dir="ltr">{u.plate_code}</bdi>
                      : <span data-testid={`culture-no-plate-${u.unit_id}`}>{t('culture.noPlate')}</span>}
                  </p>
                  {u.plate_id && readings.length === 0 && <p style={{ fontSize: 12, margin: 0 }}>{t('culture.noReadings')}</p>}
                  {readings.length > 0 && (
                    <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}
                      data-testid={`culture-readings-${u.unit_id}`}>
                      <thead>
                        <tr>
                          {data.may_record && <th><span style={srOnly}>{t('culture.record.select')}</span></th>}
                          <th style={{ textAlign: 'start' }}>{t('culture.columns.when')}</th>
                          <th>{t('culture.columns.day')}</th>
                          <th>{t('culture.columns.clean')}</th>
                          <th>{t('culture.columns.radius')}</th>
                          <th>{t('culture.columns.contamination')}</th>
                          <th style={{ textAlign: 'start' }}>{t('culture.columns.recorded')}</th>
                          <th><span style={srOnly}>{t('culture.open')}</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {readings.map((r) => (
                          <tr key={r.analysis_id} data-testid={`culture-reading-${r.analysis_id}`}
                            data-contaminated={r.contaminated ? 'true' : 'false'}>
                            {data.may_record && (
                              <td>
                                <input type="checkbox" checked={picked.includes(r.analysis_id)}
                                  onChange={() => toggle(r.analysis_id)} data-testid={`culture-pick-${r.analysis_id}`}
                                  aria-label={t('culture.record.pick', { plate: r.plate_code, when: when(r) })} />
                              </td>
                            )}
                            <td>
                              <bdi dir="ltr">{when(r)}</bdi>
                              {r.tray && <span style={{ color: 'var(--c-text-muted-fg)' }}> · {t('culture.tray')}</span>}
                            </td>
                            <td style={{ textAlign: 'end' }}><bdi dir="ltr">{r.day}</bdi></td>
                            <td style={{ textAlign: 'end' }}><bdi dir="ltr">{fmt(r.clean_area_cm2, 3)}</bdi></td>
                            <td style={{ textAlign: 'end' }}><bdi dir="ltr">{fmt(r.mean_radius_mm, 2)}</bdi></td>
                            <td style={{ textAlign: 'end' }}>
                              {r.contaminated && <span aria-hidden="true">⚠ </span>}
                              <bdi dir="ltr">{fmt(r.contamination_pct, 1)}%</bdi>
                              {r.contaminated && <span style={srOnly}> {t('culture.contaminated')}</span>}
                            </td>
                            <td data-testid={`culture-recorded-${r.analysis_id}`}>{(r.recorded || []).map((x) => outcomeLabel(x.outcome)).join(', ')}</td>
                            <td>
                              {r.photo_id && (
                                <button type="button" onClick={() => open(r)} data-testid={`culture-open-${r.analysis_id}`}
                                  title={t('culture.openTip')} style={{ fontSize: 12 }}>{t('culture.open')}</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {data.may_record && (
        <section style={box} aria-labelledby="culture-record-title" data-testid="culture-record">
          <h4 id="culture-record-title" style={{ marginTop: 0 }}>{t('culture.record.title')}</h4>
          <p style={{ fontSize: 13, marginTop: 0 }}>{t('culture.record.intro')}</p>
          {data.outcomes.length === 0 ? (
            <p style={{ fontSize: 13 }} data-testid="culture-record-none">{t('culture.record.noOutcome')}</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={label}>{t('outcome')}
                  <select style={input} value={outcome} onChange={(e) => chooseOutcome(e.target.value)}
                    data-testid="culture-record-outcome">
                    <option value="">—</option>
                    {data.outcomes.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </label>
                <label style={label}>{t('culture.record.measure')}
                  <select style={input} value={measure} onChange={(e) => setMeasure(e.target.value)}
                    disabled={!measures.length} data-testid="culture-record-measure">
                    {!measures.length && <option value="">—</option>}
                    {measures.map((m) => <option key={m} value={m}>{t(`culture.record.measures.${m}`)}</option>)}
                  </select>
                </label>
                <label style={label}>{t('timepoint')}
                  <input style={{ ...input, width: 100 }} value={timepoint} placeholder={data.design_kind === 'repeated' ? 'd7' : ''}
                    onChange={(e) => setTimepoint(e.target.value)} data-testid="culture-record-timepoint" />
                </label>
              </div>
              {data.design_kind === 'repeated' && <p style={{ fontSize: 12, margin: '0 0 8px' }}>{t('culture.record.timepointHelp')}</p>}
              {growth && (
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, margin: '0 0 8px' }}>
                  <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)}
                    data-testid="culture-record-include" />
                  {t('culture.record.include')}
                </label>
              )}
              {growth && <p style={{ fontSize: 12, margin: '0 0 8px' }}>{t('culture.record.includeHelp')}</p>}
              <button type="button" style={button('var(--c-primary)')} disabled={!canSend} onClick={record}
                data-testid="culture-record-save">{t('culture.record.button', { count: picked.length })}</button>
            </>
          )}
          {message && (
            <p role={message.kind === 'ok' ? 'status' : 'alert'} data-testid="culture-record-message"
              style={{ margin: '6px 0 0', fontSize: 13, color: message.kind === 'ok' ? undefined : 'var(--c-danger-fg)' }}>
              {message.text}</p>
          )}
        </section>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </div>
  );
}
